import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { SimpleTable, useToast, Input } from '@src/components/ui';
import AdminLayout from '../AdminLayout';
import { getBatchColumns } from './tableColumns';
import { fetchOrderBatches } from '../../../api/OrderBatchAdminApi';
import styles from './OrderBatching.module.scss';
const OrderBatching = () => {
    const { showToast } = useToast();
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const latestRequestIdRef = useRef(0);
    const fetchBatches = useCallback(async (search) => {
        const requestId = ++latestRequestIdRef.current;
        try {
            setLoading(true);
            const normalizedSearch = (search || '').trim();
            const response = await fetchOrderBatches({ search: normalizedSearch || undefined });
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            if (!response.ok) {
                throw new Error('Failed to fetch batches');
            }
            const data = response.body;
            const batchesData = Array.isArray(data) ? data : data.results || [];
            const formattedBatches = batchesData.map((batch) => ({
                id: batch.id || batch.uuid,
                batch_no: batch.batchNo || batch.batch_no || '',
                batch_timestamp: batch.batchTimestamp || batch.batch_timestamp || 'N/A',
                number_of_orders: batch.orderCount || batch.order_count || 0,
                recipient_email: batch.recipientEmail || batch.recipient_email || 'N/A',
                filename: batch.filename || '',
                csv_file: batch.csvFile || batch.csv_file,
                manufacturer: batch.manufacturer ?? null,
                formatted_batch_date: batch.formattedBatchDate || batch.formatted_batch_date,
                download_filename: batch.downloadFilename || batch.download_filename,
                created_at: batch.createdAt || batch.created_at,
                updated_at: batch.updatedAt || batch.updated_at,
            }));
            setBatches(formattedBatches);
        }
        catch (error) {
            if (requestId !== latestRequestIdRef.current) {
                return;
            }
            console.error('Error fetching batches:', error);
            showToast({ message: 'Failed to fetch order batches', variant: 'error', noBorder: true });
            setBatches([]);
        }
        finally {
            if (requestId === latestRequestIdRef.current) {
                setLoading(false);
            }
        }
    }, [showToast]);
    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            fetchBatches(searchTerm);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [fetchBatches, searchTerm]);
    const handleDownload = async (batch) => {
        try {
            const response = await fetch(`/api/v1/order-batches/${batch.id}/download/`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to download batch file');
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = batch.download_filename || batch.filename || `order_batch_${batch.id}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Error downloading batch file:', error);
            showToast({ message: 'Failed to download batch file', variant: 'error', noBorder: true });
        }
    };
    const columns = getBatchColumns(handleDownload);
    return (_jsx(AdminLayout, { children: _jsxs("div", { className: styles.container, children: [loading && _jsx("div", { children: "Loading..." }), _jsxs("div", { className: styles.tableWrapper, children: [_jsx("div", { className: styles.searchRow, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Input, { value: searchTerm, onChange: (value) => setSearchTerm(value || ''), placeholder: "Search by batch no, recipient email, or filename", width: "420px", showIcon: true, iconWidth: "16px", iconHeight: "16px" }), !!searchTerm && (_jsx("button", { type: "button", className: styles.clearButton, onClick: () => setSearchTerm(''), "aria-label": "Clear search", children: "x" }))] }) }), _jsx(SimpleTable, { columns: columns, data: batches, rowHeight: "60px", tableHeading: "Order Batching", titleStyle: {
                                color: '#000',
                                fontFamily: 'Nunito',
                                fontSize: '24px',
                                fontStyle: 'normal',
                                fontWeight: 600,
                                lineHeight: '28px',
                            }, hideTopBorder: true, enablePagination: true, paginationText: "Batches per page", pageSize: 20 })] })] }) }));
};
export default OrderBatching;
